import React, { useState, useEffect } from "react";
import {
  Table,
  Avatar,
  Typography,
  Button,
  Input,
  Space,
  Modal,
  Form,
  message,
  DatePicker,
  TimePicker,
  Row,
  Col,
  Select,
  Popconfirm,
} from "antd";
import { UserOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next"; // ✅ translations
import { companyService } from "../../app/services";
import dayjs from "dayjs";

const { Title } = Typography;
const { Option } = Select;

const TeamMembersPage = ({ company }) => {
  const [team, setTeam] = useState([]);
  const [search, setSearch] = useState("");
  const [services, setServices] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [form] = Form.useForm();
  const [breakTimes, setBreakTimes] = useState([{ from: null, to: null }]);
  const { t } = useTranslation();

  useEffect(() => {
    const init = async () => {
      const loadedServices = await fetchServices();
      await fetchTeam(loadedServices);
    };
    init();
  }, []);

  // Fetch services
  const fetchServices = async () => {
    try {
      const response = await companyService.getCompanyServicesByParameter(
        company,
        null,
        null
      );
      if (response && Array.isArray(response)) {
        setServices(response);
        return response;
      }
      return [];
    } catch (err) {
      console.error(err);
      message.error(t("TeamMembersPage.load_services_fail"));
      return [];
    }
  };

  // Fetch team
  const fetchTeam = async (loadedServices = services) => {
    try {
      const response = await companyService.getCompanyEmployees(company);

      const teamWithKey = response.data.map((employee) => {
        const employeeServiceIds =
          employee.employeeCompanyServices?.map(
            (x) => x.companyServiceTypeId
          ) || [];

        const matchedServices = loadedServices
          .filter((s) => employeeServiceIds.includes(s.id))
          .map((s) => t(s.name)); // ✅ translate service names

        return {
          ...employee,
          key: employee.PlanningId,
          serviceIds: employeeServiceIds,
          services: matchedServices,
        };
      });

      setTeam(teamWithKey);
    } catch (err) {
      console.error(err);
    }
  };

  // Save member
  const handleAddEdit = async (values) => {
    try {
      const payload = {
        ...values,
        startDate: values.startDate?.format("YYYY-MM-DD"),
        endDate: values.endDate?.format("YYYY-MM-DD"),
        meetingTime: values.meetingTime?.format("HH:mm:ss"),
        breakTimes: transformBreakTimes(values),
        services: values.services.map((serviceId) => ({
          id: null,
          CompanyEmployeeId: values.planningId || null,
          CompanyServiceTypeId: serviceId,
        })),
      };

      if (editingMember) {
        await companyService.modifyEmployeetoCompany(
          company,
          payload.name,
          payload.planningId,
          payload.startDate,
          payload.endDate,
          payload.meetingTime,
          payload.breakTimes,
          payload.services
        );
        message.success(t("TeamMembersPage.update_success"));
      } else {
        await companyService.addEmployeetoCompany(
          company,
          payload.name,
          payload.startDate,
          payload.endDate,
          payload.meetingTime,
          payload.breakTimes,
          payload.services
        );
        message.success(t("TeamMembersPage.add_success"));
      }

      fetchTeam();
      setIsModalOpen(false);
      form.resetFields();
      setEditingMember(null);
      setBreakTimes([{ from: null, to: null }]);
    } catch (err) {
      console.error(err);
      message.error(t("TeamMembersPage.save_fail"));
    }
  };

  const transformBreakTimes = (data) => {
    const breakTimes = [];
    for (const key in data) {
      if (key.startsWith("breakTimeFrom")) {
        const index = key.slice("breakTimeFrom".length);
        const from = data[key];
        const to = data[`breakTimeTo${index}`];
        if (from && to) {
          breakTimes.push({
            from: from.format("HH:mm:ss"),
            to: to.format("HH:mm:ss"),
          });
        }
      }
    }
    return breakTimes;
  };

  // Edit
  const handleEditClick = (record) => {
    setEditingMember(record);

    const formatted = {
      ...record,
      startDate: record.startDate ? dayjs(record.startDate) : null,
      endDate: record.endDate ? dayjs(record.endDate) : null,
      meetingTime: record.meetingTime
        ? dayjs(record.meetingTime, "HH:mm:ss")
        : null,
      services: record.serviceIds || [],
    };

    record.breakTimes?.forEach((bt, index) => {
      formatted[`breakTimeFrom${index}`] = dayjs(bt.from, "HH:mm:ss");
      formatted[`breakTimeTo${index}`] = dayjs(bt.to, "HH:mm:ss");
    });

    form.setFieldsValue(formatted);
    setBreakTimes(record.breakTimes || [{ from: null, to: null }]);
    setIsModalOpen(true);
  };

  // Delete
  const handleDelete = async (record) => {
    try {
      await companyService.deleteEmployeetoCompany(company, record.planningId);
      message.success(t("TeamMembersPage.delete_success"));
      fetchTeam();
    } catch (err) {
      console.error(err);
      message.error(t("TeamMembersPage.delete_fail"));
    }
  };

  const handleAddBreak = () => {
    setBreakTimes([...breakTimes, { from: null, to: null }]);
  };

  const columns = [
    {
      title: t("TeamMembersPage.fullname"),
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Space>
          <Avatar
            src={record.avatar}
            icon={!record.avatar && <UserOutlined />}
            style={{ backgroundColor: !record.avatar ? "#7265e6" : undefined }}
          >
            {!record.avatar && text[0]}
          </Avatar>
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: t("TeamMembersPage.contact"),
      dataIndex: "contact",
      key: "contact",
      render: (contact) => (
        <span style={{ color: "#1677ff" }}>{contact || "—"}</span>
      ),
    },
    {
      title: t("TeamMembersPage.role"),
      dataIndex: "role",
      key: "role",
      render: (role) => role || t("TeamMembersPage.role_default"),
    },
    {
      title: t("TeamMembersPage.services"),
      dataIndex: "services",
      key: "services",
      render: (services) =>
        services?.length
          ? services.join(", ")
          : t("TeamMembersPage.services_none"),
    },
    {
      title: t("TeamMembersPage.action"),
      key: "action",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => handleEditClick(record)}>
            {t("TeamMembersPage.edit")}
          </Button>
          <Popconfirm
            title={t("TeamMembersPage.delete_title")}
            description={t("TeamMembersPage.delete_confirm")}
            okText={t("TeamMembersPage.yes")}
            cancelText={t("TeamMembersPage.no")}
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" danger>
              {t("TeamMembersPage.delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Title level={3} style={{ marginBottom: 24 }}>
        {t("TeamMembersPage.title")}
      </Title>

      <Space style={{ marginBottom: 16, width: "100%" }} align="center">
        <Input.Search
          placeholder={t("TeamMembersPage.search_placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 300 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsModalOpen(true)}
        >
          {t("TeamMembersPage.add")}
        </Button>
      </Space>

      <Table
        rowKey="PlanningId"
        columns={columns}
        dataSource={team.filter((m) =>
          m.name.toLowerCase().includes(search.toLowerCase())
        )}
        pagination={false}
        bordered={false}
        style={{
          background: "white",
          borderRadius: 8,
          boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
        }}
      />

      {/* Modal */}
      <Modal
        title={editingMember ? t("TeamMembersPage.edit") : t("TeamMembersPage.add")}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingMember(null);
          form.resetFields();
          setBreakTimes([{ from: null, to: null }]);
        }}
        footer={null}
        width={700}
      >
        <Form layout="vertical" form={form} onFinish={handleAddEdit}>
          <Form.Item style={{ display: "none" }} name="planningId">
            <Input type="hidden" />
          </Form.Item>

          <Form.Item
            name="name"
            label={t("TeamMembersPage.fullname")}
            rules={[{ required: true, message: t("TeamMembersPage.fullname_required") }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="contact" label={t("TeamMembersPage.contact")}>
            <Input />
          </Form.Item>

          <Form.Item name="role" label={t("TeamMembersPage.role")}>
            <Input placeholder={t("TeamMembersPage.role_placeholder")} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startDate"
                label={t("TeamMembersPage.startDate")}
                rules={[{ required: true, message: t("TeamMembersPage.startDate_required") }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endDate"
                label={t("TeamMembersPage.endDate")}
                rules={[{ required: true, message: t("TeamMembersPage.endDate_required") }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="meetingTime"
            label={t("TeamMembersPage.meetingTime")}
            rules={[{ required: true, message: t("TeamMembersPage.meetingTime_required") }]}
          >
            <TimePicker style={{ width: "100%" }} format="HH:mm:ss" />
          </Form.Item>

          <Title level={5} style={{ marginTop: 16 }}>
            {t("TeamMembersPage.breakTimes")}
          </Title>
          {breakTimes.map((_, index) => (
            <Row gutter={16} key={index} style={{ marginBottom: 12 }}>
              <Col span={12}>
                <Form.Item
                  name={`breakTimeFrom${index}`}
                  label={t("TeamMembersPage.breakFrom")}
                  rules={[{ required: true, message: t("TeamMembersPage.breakFrom_required") }]}
                >
                  <TimePicker style={{ width: "100%" }} format="HH:mm:ss" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={`breakTimeTo${index}`}
                  label={t("TeamMembersPage.breakTo")}
                  rules={[{ required: true, message: t("TeamMembersPage.breakTo_required") }]}
                >
                  <TimePicker style={{ width: "100%" }} format="HH:mm:ss" />
                </Form.Item>
              </Col>
            </Row>
          ))}
          <Button type="dashed" onClick={handleAddBreak} block>
            + {t("TeamMembersPage.addBreak")}
          </Button>

          <Form.Item
            name="services"
            label={t("TeamMembersPage.services")}
            rules={[{ required: true, message: t("TeamMembersPage.services_required") }]}
          >
            <Select
              mode="multiple"
              placeholder={t("TeamMembersPage.services_placeholder")}
              optionLabelProp="label"
            >
              {services.map((service) => (
                <Option
                  key={service.id}
                  value={service.id}
                  label={t(service.name)}
                >
                  <Space>
                    {service.icon && (
                      <span
                        dangerouslySetInnerHTML={{ __html: service.icon }}
                        style={{
                          width: 20,
                          height: 20,
                          display: "inline-block",
                          lineHeight: 0,
                        }}
                      />
                    )}
                    <span>{t(service.name)}</span>
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {editingMember ? t("TeamMembersPage.update") : t("TeamMembersPage.add")}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TeamMembersPage;
